import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Button,
  Select,
  Divider,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { AnimatedEventStream, type FormattedEvent } from '../../components/AnimatedEventStream';
import {
  useJobDefinitionsQuery,
  useJobsQuery,
  useJobGroupsQuery,
  useJobEventsQuery,
  useRunJobMutation,
  useAbortJobMutation,
  type JobStatus,
  type JobDefinition,
  type JobRecord,
  type JobGroupRecord,
  type JobEventRecord,
  statusVariants,
  statusLabels,
  formatDateTime,
  formatRelative,
  safeStringify,
} from './useSuperadminData';

const STATUS_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'All statuses' },
  { id: 'queued', label: 'Queued' },
  { id: 'running', label: 'Running' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed' },
  { id: 'canceled', label: 'Canceled' },
];

const formatJobEvent = (event: JobEventRecord): FormattedEvent => {
  const timestamp = new Date(event.createdAt).toLocaleTimeString();
  const baseText = `${timestamp} • ${event.eventType}`;
  const statusMap: Record<string, { variant: FormattedEvent['status']; prefix?: string }> = {
    'job.queued': { variant: 'info', prefix: 'Queued' },
    'job.requeued': { variant: 'notice', prefix: 'Retry scheduled' },
    'job.completed': { variant: 'positive', prefix: 'Completed' },
    'job.failed': { variant: 'negative', prefix: 'Failed' },
    'job-group.created': { variant: 'neutral', prefix: 'Group created' },
    status: { variant: 'info' },
    error: { variant: 'negative' },
    complete: { variant: 'positive' },
    log: { variant: 'neutral' },
    'tool-call': { variant: 'notice', prefix: 'Tool call' },
  };

  const mapping = statusMap[event.eventType] ?? { variant: 'neutral' as FormattedEvent['status'] };
  const detail =
    event.data && Object.keys(event.data).length > 0 ? safeStringify(event.data) : undefined;

  return {
    text: mapping.prefix ? `${mapping.prefix}: ${baseText}` : baseText,
    status: mapping.variant,
    statusLabel: mapping.prefix ?? event.eventType,
    detail,
  };
};

const jobDefinitionColumns: ColumnDef<StockFeatures, JobDefinition>[] = [
  {
    accessorKey: 'jobType',
    header: 'Job Type',
    cell: ({ row }) => <Text weight="medium">{row.original.jobType}</Text>,
  },
  {
    accessorKey: 'maxAttempts',
    header: 'Max Attempts',
    cell: ({ row }) => <Text>{row.original.maxAttempts ?? 'Default'}</Text>,
  },
  {
    accessorKey: 'timeoutSeconds',
    header: 'Timeout (s)',
    cell: ({ row }) => <Text>{row.original.timeoutSeconds ?? 'Default'}</Text>,
  },
];

const jobColumns: ColumnDef<StockFeatures, JobRecord>[] = [
  {
    accessorKey: 'jobType',
    header: 'Job Type',
    cell: ({ row }) => <Text weight="medium">{row.original.jobType}</Text>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={statusLabels[row.original.status]}
        color={badgeVariantToTagColor(statusVariants[row.original.status])}
        hideDot
      />
    ),
  },
  {
    accessorKey: 'orgId',
    header: 'Org',
    cell: ({ row }) => <Text size="sm">{row.original.orgId ?? '—'}</Text>,
  },
  {
    accessorKey: 'runAt',
    header: 'Scheduled',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {formatDateTime(row.original.runAt)}
      </Text>
    ),
  },
  {
    accessorKey: 'startedAt',
    header: 'Started',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {formatRelative(row.original.startedAt)}
      </Text>
    ),
  },
  {
    accessorKey: 'attempts',
    header: 'Attempts',
    cell: ({ row }) => (
      <Text size="sm">
        {row.original.attempts}/{row.original.maxAttempts}
      </Text>
    ),
  },
];

const jobGroupColumns: ColumnDef<StockFeatures, JobGroupRecord>[] = [
  {
    accessorKey: 'jobType',
    header: 'Job Type',
    cell: ({ row }) => <Text weight="medium">{row.original.jobType}</Text>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <TagBadge
        name={statusLabels[row.original.status]}
        color={badgeVariantToTagColor(statusVariants[row.original.status])}
        hideDot
      />
    ),
  },
  {
    accessorKey: 'queuedCount',
    header: 'Counts',
    cell: ({ row }) => (
      <Text size="sm">
        {row.original.completedCount}/{row.original.queuedCount + row.original.completedCount} done
      </Text>
    ),
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {formatRelative(row.original.updatedAt)}
      </Text>
    ),
  },
];

export function SuperadminJobs() {
  const queryClient = useQueryClient();

  const [jobTypeFilter, setJobTypeFilter] = React.useState<string>('all');
  const [statusFilter, setStatusFilter] = React.useState<'all' | JobStatus>('all');
  const [selectedJobId, setSelectedJobId] = React.useState<string | null>(() => {
    const jobId = new URLSearchParams(window.location.search).get('jobId');
    const uuidPattern = /^[0-9a-fA-F-]{36}$/;
    return jobId && uuidPattern.test(jobId) ? jobId : null;
  });

  const [runJobType, setRunJobType] = React.useState<string | null>(null);
  const [runOrgId, setRunOrgId] = React.useState('');
  const [runPayload, setRunPayload] = React.useState<string>('{}');
  const [brokerEntityId, setBrokerEntityId] = React.useState('');
  const [brokerOrgId, setBrokerOrgId] = React.useState('');
  const [runError, setRunError] = React.useState<string | null>(null);

  const jobDefinitionsQuery = useJobDefinitionsQuery();
  const jobsQuery = useJobsQuery(jobTypeFilter, statusFilter);
  const jobGroupsQuery = useJobGroupsQuery();
  const jobEventsQuery = useJobEventsQuery(selectedJobId);
  const runJobMutation = useRunJobMutation();
  const abortJobMutation = useAbortJobMutation();

  React.useEffect(() => {
    if (!runJobType && jobDefinitionsQuery.data?.definitions.length) {
      setRunJobType(jobDefinitionsQuery.data.definitions[0]!.jobType);
    }
  }, [jobDefinitionsQuery.data, runJobType]);

  const selectedJob = React.useMemo(() => {
    if (!selectedJobId) return null;
    return jobsQuery.data?.jobs.find((job) => job.id === selectedJobId) ?? null;
  }, [jobsQuery.data, selectedJobId]);

  React.useEffect(() => {
    if (!selectedJobId && jobsQuery.data?.jobs.length) {
      setSelectedJobId(jobsQuery.data.jobs[0]!.id);
    } else if (selectedJobId && jobsQuery.data?.jobs.every((job) => job.id !== selectedJobId)) {
      setSelectedJobId(jobsQuery.data.jobs[0]?.id ?? null);
    }
  }, [jobsQuery.data, selectedJobId]);

  const jobTypeOptions = React.useMemo(() => {
    const definitions = jobDefinitionsQuery.data?.definitions ?? [];
    return [
      { id: 'all', label: 'All job types' },
      ...definitions.map((def) => ({ id: def.jobType, label: def.jobType })),
    ];
  }, [jobDefinitionsQuery.data]);

  const runJobTypeOptions = React.useMemo(() => {
    return (jobDefinitionsQuery.data?.definitions ?? []).map((def) => ({
      id: def.jobType,
      label: def.jobType,
    }));
  }, [jobDefinitionsQuery.data]);

  const handleRunJob = () => {
    setRunError(null);
    if (!runJobType) {
      setRunError('Select a job type to run.');
      return;
    }

    let parsedPayload: Record<string, unknown> = {};
    const trimmed = runPayload.trim();
    if (trimmed.length > 0) {
      try {
        parsedPayload = JSON.parse(trimmed);
      } catch (error) {
        setRunError(error instanceof Error ? error.message : 'Payload must be valid JSON');
        return;
      }
    }

    const mutationPayload: {
      jobType: string;
      payload: Record<string, unknown>;
      orgId?: string;
      metadata?: Record<string, unknown>;
      broker?: { entityId: string; orgId: string };
    } = {
      jobType: runJobType,
      payload: parsedPayload,
    };

    if (runOrgId.trim().length > 0) {
      mutationPayload.orgId = runOrgId.trim();
    }

    const metadata: Record<string, unknown> = {};
    if (runOrgId.trim()) metadata.orgId = runOrgId.trim();
    if (brokerEntityId.trim()) metadata.brokerEntityId = brokerEntityId.trim();
    if (Object.keys(metadata).length > 0) {
      mutationPayload.metadata = metadata;
    }

    if (brokerEntityId.trim() && brokerOrgId.trim()) {
      mutationPayload.broker = {
        entityId: brokerEntityId.trim(),
        orgId: brokerOrgId.trim(),
      };
    }

    runJobMutation.mutate(mutationPayload, {
      onSuccess: (data) => {
        if (data.job?.id) {
          setSelectedJobId(data.job.id);
        }
      },
    });
  };

  const jobEvents = React.useMemo(() => {
    const events = jobEventsQuery.data?.events ?? [];
    // Oldest first: the API returns newest-first and carries no sequence, so the
    // timestamp orders the stream with the id as a stable tiebreaker.
    return [...events].sort((a, b) => {
      const diff = Date.parse(a.createdAt) - Date.parse(b.createdAt);
      return diff !== 0 ? diff : a.id.localeCompare(b.id);
    });
  }, [jobEventsQuery.data]);

  return (
    <Box className="space-y-6">
      <Flex direction="column" gap="xl">
        <Card padding="lg">
          <CardHeader>
            <Heading level={3}>Run a Job</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Execute any registered job type for diagnostics or maintenance.
            </Text>
          </CardHeader>
          <CardContent>
            <Flex direction="column" gap="lg">
              <Flex gap="md" wrap>
                <Select
                  label="Job type"
                  placeholder="Select job type"
                  items={runJobTypeOptions}
                  selectedKey={runJobType ?? undefined}
                  onSelectionChange={(key) => setRunJobType(String(key))}
                  className="min-w-[240px]"
                  isDisabled={jobDefinitionsQuery.isLoading}
                />
                <Box className="min-w-[200px]">
                  <Text size="xs" className="text-muted-foreground mb-1 block">
                    Organization ID (optional)
                  </Text>
                  <input
                    value={runOrgId}
                    onChange={(event) => setRunOrgId(event.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    placeholder="00000000-0000-0000-0000-000000000000"
                  />
                </Box>
                <Box className="min-w-[200px]">
                  <Text size="xs" className="text-muted-foreground mb-1 block">
                    Broker entity ID (optional)
                  </Text>
                  <input
                    value={brokerEntityId}
                    onChange={(event) => setBrokerEntityId(event.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    placeholder="Entity/job subscription identifier"
                  />
                </Box>
                <Box className="min-w-[200px]">
                  <Text size="xs" className="text-muted-foreground mb-1 block">
                    Broker org ID (optional)
                  </Text>
                  <input
                    value={brokerOrgId}
                    onChange={(event) => setBrokerOrgId(event.target.value)}
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                    placeholder="Org for broker stream"
                  />
                </Box>
              </Flex>

              <Box>
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Payload (JSON)
                </Text>
                <textarea
                  value={runPayload}
                  onChange={(event) => setRunPayload(event.target.value)}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm font-mono min-h-[140px] bg-background"
                  spellCheck={false}
                />
              </Box>

              {runError && <Text className="text-destructive text-sm">{runError}</Text>}
              {runJobMutation.isError && (
                <Text className="text-destructive text-sm">
                  {runJobMutation.error instanceof Error
                    ? runJobMutation.error.message
                    : 'Failed to run job'}
                </Text>
              )}
              {runJobMutation.isSuccess && (
                <Text className="text-success text-sm">
                  Job enqueued successfully (ID {runJobMutation.data.job.id})
                </Text>
              )}

              <Button onPress={handleRunJob} isDisabled={runJobMutation.isPending || !runJobType}>
                {runJobMutation.isPending ? 'Submitting…' : 'Run Job'}
              </Button>

              <Divider />

              {jobDefinitionsQuery.isLoading ? (
                <Flex align="center" justify="center" className="py-4">
                  <LoadingOrbit size="sm" />
                </Flex>
              ) : jobDefinitionsQuery.data?.definitions?.length ? (
                <DataTable
                  columns={jobDefinitionColumns}
                  data={jobDefinitionsQuery.data.definitions}
                />
              ) : (
                <Text className="text-muted-foreground">No job definitions registered yet.</Text>
              )}
            </Flex>
          </CardContent>
        </Card>

        <Card padding="lg">
          <CardHeader>
            <Flex align="center" justify="between" wrap gap="md">
              <div>
                <Heading level={3}>Recent Jobs</Heading>
                <Text size="sm" className="text-muted-foreground mt-1">
                  Filter and inspect the latest jobs across the platform.
                </Text>
              </div>
              <Flex gap="md" wrap align="center">
                <Select
                  placeholder="Filter by job type"
                  items={jobTypeOptions}
                  selectedKey={jobTypeFilter}
                  onSelectionChange={(key) => setJobTypeFilter(String(key))}
                  className="min-w-[180px]"
                  isDisabled={jobDefinitionsQuery.isLoading}
                />
                <Select
                  placeholder="Filter by status"
                  items={STATUS_OPTIONS}
                  selectedKey={statusFilter}
                  onSelectionChange={(key) => setStatusFilter(key as JobStatus | 'all')}
                  className="min-w-[160px]"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    queryClient
                      .invalidateQueries({ queryKey: ['superadmin-jobs'] })
                      .catch(() => {});
                  }}
                >
                  Refresh
                </Button>
              </Flex>
            </Flex>
          </CardHeader>
          <CardContent>
            {jobsQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-10">
                <LoadingOrbit size="lg" />
              </Flex>
            ) : jobsQuery.isError ? (
              <Text className="text-destructive">
                {jobsQuery.error instanceof Error ? jobsQuery.error.message : 'Failed to load jobs'}
              </Text>
            ) : jobsQuery.data?.jobs?.length ? (
              <DataTable
                columns={jobColumns}
                data={jobsQuery.data.jobs}
                onRowClick={(job) => setSelectedJobId(job.id)}
              />
            ) : (
              <Text className="text-muted-foreground">No jobs found for the selected filter.</Text>
            )}
          </CardContent>
        </Card>

        <Flex direction="column" gap="lg" wrap className="lg:flex-row">
          <Card padding="lg" className="flex-1 min-w-[320px]">
            <CardHeader>
              <Heading level={3}>Job Details & Events</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Inspect metadata, payload, and live event stream for the selected job.
              </Text>
            </CardHeader>
            <CardContent>
              {!selectedJob ? (
                <Text className="text-muted-foreground">Select a job to view details.</Text>
              ) : (
                <Flex direction="column" gap="lg">
                  <Box>
                    <Heading level={4} className="text-base mb-2">
                      Metadata
                    </Heading>
                    <Flex direction="column" gap="sm">
                      <Text size="sm">
                        <span className="text-muted-foreground">Job ID:</span> {selectedJob.id}
                      </Text>
                      <Flex align="center" gap="sm" className="text-sm">
                        <Text size="sm" className="m-0">
                          <span className="text-muted-foreground">Status:</span>
                        </Text>
                        <TagBadge
                          name={statusLabels[selectedJob.status]}
                          color={badgeVariantToTagColor(statusVariants[selectedJob.status])}
                          hideDot
                        />
                        {(selectedJob.status === 'queued' || selectedJob.status === 'running') && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onPress={() => abortJobMutation.mutate({ jobId: selectedJob.id })}
                            isDisabled={abortJobMutation.isPending}
                          >
                            {abortJobMutation.isPending ? 'Aborting…' : 'Abort'}
                          </Button>
                        )}
                      </Flex>
                      {abortJobMutation.isSuccess && (
                        <Text size="sm" className="text-success">
                          Job abort requested ({abortJobMutation.data.status})
                        </Text>
                      )}
                      {abortJobMutation.isError && (
                        <Text size="sm" className="text-destructive">
                          {abortJobMutation.error instanceof Error
                            ? abortJobMutation.error.message
                            : 'Failed to abort job'}
                        </Text>
                      )}
                      <Text size="sm">
                        <span className="text-muted-foreground">Org:</span>{' '}
                        {selectedJob.orgId ?? '—'}
                      </Text>
                      <Text size="sm">
                        <span className="text-muted-foreground">Group:</span>{' '}
                        {selectedJob.groupId ?? '—'}
                      </Text>
                      <Text size="sm">
                        <span className="text-muted-foreground">Created:</span>{' '}
                        {formatDateTime(selectedJob.createdAt)}
                      </Text>
                      <Text size="sm">
                        <span className="text-muted-foreground">Started:</span>{' '}
                        {formatDateTime(selectedJob.startedAt)}
                      </Text>
                      <Text size="sm">
                        <span className="text-muted-foreground">Completed:</span>{' '}
                        {formatDateTime(selectedJob.completedAt)}
                      </Text>
                      {selectedJob.lastError && (
                        <Text size="sm" className="text-destructive">
                          {selectedJob.lastError}
                        </Text>
                      )}
                    </Flex>
                  </Box>

                  <Flex gap="lg" wrap>
                    <Box className="flex-1 min-w-[240px]">
                      <Heading level={4} className="text-base mb-2">
                        Payload
                      </Heading>
                      <pre className="bg-muted/40 border border-border rounded-md p-3 text-xs overflow-auto max-h-48">
                        {safeStringify(selectedJob.payload)}
                      </pre>
                    </Box>
                    <Box className="flex-1 min-w-[240px]">
                      <Heading level={4} className="text-base mb-2">
                        Metadata
                      </Heading>
                      <pre className="bg-muted/40 border border-border rounded-md p-3 text-xs overflow-auto max-h-48">
                        {safeStringify(selectedJob.metadata ?? {})}
                      </pre>
                    </Box>
                  </Flex>

                  <Box>
                    <Heading level={4} className="text-base mb-2">
                      Event Stream
                    </Heading>
                    {jobEventsQuery.isLoading ? (
                      <Flex align="center" justify="center" className="py-6">
                        <LoadingOrbit size="sm" />
                      </Flex>
                    ) : jobEvents.length > 0 ? (
                      <AnimatedEventStream
                        events={jobEvents}
                        formatEvent={formatJobEvent}
                        getEventKey={(event) => event.id}
                      />
                    ) : (
                      <Text className="text-muted-foreground">No events recorded yet.</Text>
                    )}
                  </Box>
                </Flex>
              )}
            </CardContent>
          </Card>

          <Card padding="lg" className="flex-1 min-w-[280px]">
            <CardHeader>
              <Heading level={3}>Job Groups</Heading>
              <Text size="sm" className="text-muted-foreground mt-1">
                Snapshot of the most recent job groups and their progress.
              </Text>
            </CardHeader>
            <CardContent>
              {jobGroupsQuery.isLoading ? (
                <Flex align="center" justify="center" className="py-6">
                  <LoadingOrbit size="sm" />
                </Flex>
              ) : jobGroupsQuery.isError ? (
                <Text className="text-destructive">
                  {jobGroupsQuery.error instanceof Error
                    ? jobGroupsQuery.error.message
                    : 'Failed to load job groups'}
                </Text>
              ) : jobGroupsQuery.data?.groups?.length ? (
                <DataTable
                  columns={jobGroupColumns}
                  data={jobGroupsQuery.data.groups}
                  initialSorting={[{ id: 'updatedAt', desc: true }]}
                />
              ) : (
                <Text className="text-muted-foreground">No job groups yet.</Text>
              )}
            </CardContent>
          </Card>
        </Flex>
      </Flex>
    </Box>
  );
}
